/**
 * سربرگِ «پرچم‌های رخداد» در ادمین — همه‌ی منطقِ بی‌سرور (مایگریشن 0200).
 *
 * SQL فقط فهرست می‌دهد (`admin_event_issues`). این‌جا:
 *   ۱. **خواندنِ متنِ پرچم** — `goout-issue:wrong:tag:جاز` یعنی «اطلاعات
 *      اشتباه › تگ‌ها › جاز». همان قراردادی که دو اپ با `lib/issues.ts`
 *      می‌نویسند، پس از همان تابع‌ها خوانده می‌شود و هرگز از هم جدا نمی‌افتند.
 *   ۲. **وضعیت** — باز / تاییدشده / ردشده، به‌علاوه‌ی «اصلاح شد» و «کاربر پس
 *      گرفت».
 *   ۳. **فیلتر و گروه‌بندی** برای فهرست.
 *   ۴. **آمار** — همه‌ی عددها و ستون‌های نمودارها.
 *   ۵. **اصلاح** — کدام ستونِ رخداد جوابِ کدام پرچم است، و ساختنِ `patch`ی که
 *      فقط چیزِ عوض‌شده را دارد.
 *
 * فارسیِ درجا و نه `t()`: اپِ ادمین i18n ندارد (نگاه کن به بالای ItemDetail).
 * برچسب‌ها همان کلماتِ فارسیِ منوی پرچم در اپ‌اند تا ادمین همان چیزی را
 * بخواند که کاربر زده.
 */
import { parseFieldDetail, parseIssueText, type IssueKind } from './issues.ts';
import type { Tag } from '../domain/types.ts';

/* ─────────────────────────── شکلِ داده ─────────────────────────── */

export type IssueVerdict = 'accepted' | 'rejected';

/** یک سطر از `admin_event_issues().issues`. */
export interface EventIssueRow {
  /** `null` یعنی کاربر پرچم را پس گرفته و فقط تصمیمِ ادمین مانده. */
  issue_id: number | null;
  review_id: number | null;
  event_id: string;
  user_id: string | null;
  who: string | null;
  username: string | null;
  /** حسابِ داخلی/تستی (0119) — آمار می‌تواند کنارش بگذارد. */
  internal: boolean;
  text: string;
  at: string;
  withdrawn: boolean;
  verdict: IssueVerdict | null;
  note: string | null;
  decided_by: string | null;
  decided_at: string | null;
  fixed_at: string | null;
  fixed_fields: string[];
}

/** یک رخداد از `admin_event_issues().events` — فقط فیلدهایی که اصلاح لازم دارد. */
export interface EventIssueEvent {
  id: string;
  short_id: number | null;
  root_id: string | null;
  kind: string;
  status: string;
  source: string | null;
  crawler: boolean;
  city: string | null;
  title: string;
  description: string | null;
  starts_at: string | null;
  ends_at: string | null;
  is_free: boolean;
  price_note: string | null;
  url: string | null;
  ticket_url: string | null;
  image_url: string | null;
  image_invalid: boolean;
  address: string | null;
  category_id: number | null;
  tag_ids: number[];
  locked_fields: string[];
}

export interface EventIssuesData {
  issues: EventIssueRow[];
  events: Record<string, EventIssueEvent>;
}

/* ─────────────────────────── ۱ ــ خواندنِ متن ─────────────────────────── */

export const KIND_LABEL: Record<IssueKind, string> = {
  missed: 'جاافتاده',
  wrong: 'اشتباه',
  inappropriate: 'نامناسب',
  duplicate: 'تکراری',
  other: 'سایر',
};

export const KIND_ICON: Record<IssueKind, string> = {
  missed: '➕', wrong: '✏️', inappropriate: '🚫', duplicate: '👯', other: '💬',
};

/** همان ترتیب و همان کلماتِ `ISSUE_FIELDS` در منوی اپ. */
export const FIELD_LABEL: Record<string, string> = {
  title: 'عنوان',
  image: 'عکس',
  description: 'توضیحات',
  description_format: 'قالبِ توضیحات',
  when: 'زمان',
  where: 'نشانی',
  price: 'قیمت',
  ticket: 'دکمه‌ی خریدِ بلیط',
  link: 'لینکِ منبع',
  tag: 'تگ‌ها',
};

const DUPLICATE_LABEL: Record<string, string> = {
  same_subject_different_time: 'موضوعِ یکسان، تاریخ یا ساعتِ متفاوت',
  complete_duplicate: 'کاملاً تکراری',
};

/** پرچم، باز شده. */
export interface ParsedEventIssue {
  kind: IssueKind;
  /** فقط برای `missed`/`wrong`: کلیدِ `FIELD_LABEL`. فیلدِ ناشناخته همان‌طور می‌ماند. */
  field: string | null;
  /**
   * برای تگ: نامِ تگی که گفته شده (برای `wrong` = «این تگ غلط است»، برای
   * `missed` = «این تگ را کم دارد»). برای بقیه: مقدارِ درستی که کاربر نوشته
   * (`title=…`) یا متنِ آزادِ «نامناسب»/«سایر».
   */
  value: string | null;
  /** یک خطِ خوانا برای فهرست. */
  summary: string;
}

/**
 * `tag:جاز` در detail یعنی «این تگ». جداکننده‌اش `:` است و نه `=` (نگاه کن
 * به `FIELD_FIX_SEP` در `issues.ts`)، پس `parseFieldDetail` تگ را نمی‌شناسد و
 * این‌جا جدا باز می‌شود.
 */
export function parseEventIssue(text: string): ParsedEventIssue {
  const { kind, detail } = parseIssueText(text);
  if (kind === 'missed' || kind === 'wrong') {
    if (detail === 'tag' || detail.startsWith('tag:')) {
      const value = detail.startsWith('tag:') ? detail.slice(4).trim() || null : null;
      const verb = kind === 'wrong' ? 'تگِ اشتباه' : 'تگِ جاافتاده';
      return { kind, field: 'tag', value, summary: value ? `${verb}: ${value}` : `${KIND_LABEL[kind]} › تگ‌ها` };
    }
    const { field, fix } = parseFieldDetail(detail);
    const label = FIELD_LABEL[field] ?? field;
    return {
      kind, field, value: fix,
      summary: `${KIND_LABEL[kind]} › ${label}${fix ? ` — درستش: «${fix}»` : ''}`,
    };
  }
  if (kind === 'duplicate') {
    return { kind, field: null, value: null, summary: `تکراری › ${DUPLICATE_LABEL[detail] ?? detail}` };
  }
  return { kind, field: null, value: detail, summary: `${KIND_LABEL[kind]}: ${detail}` };
}

/* ─────────────────────────── ۲ ــ وضعیت ─────────────────────────── */

export type IssueStatus = 'open' | 'accepted' | 'rejected';

export const STATUS_LABEL: Record<IssueStatus, string> = {
  open: 'باز', accepted: 'تاییدشده', rejected: 'ردشده',
};

export const issueStatus = (row: Pick<EventIssueRow, 'verdict'>): IssueStatus => row.verdict ?? 'open';

/** کلیدِ یکتای سطر — پرچمِ پس‌گرفته `issue_id` ندارد. */
export const rowKey = (row: Pick<EventIssueRow, 'issue_id' | 'review_id'>): string =>
  row.issue_id != null ? `i${row.issue_id}` : `r${row.review_id}`;

/** فقط پرچمِ زنده تصمیم‌گرفتنی است؛ پس‌گرفته فقط سابقه است. */
export const isActionable = (row: Pick<EventIssueRow, 'issue_id' | 'withdrawn'>): boolean =>
  row.issue_id != null && !row.withdrawn;

/* ─────────────────────────── ۳ ــ فیلتر ─────────────────────────── */

export interface IssueFilter {
  status?: IssueStatus | 'all';
  kind?: IssueKind | 'all';
  field?: string | 'all';
  /** جستجو در متنِ پرچم، عنوانِ رخداد، نامِ گزارش‌دهنده، شناسه‌ی عددی. */
  q?: string;
  /** پیش‌فرض: حساب‌های داخلی هم دیده می‌شوند. */
  hideInternal?: boolean;
  hideWithdrawn?: boolean;
  eventId?: string | null;
}

export function filterIssues(data: EventIssuesData, f: IssueFilter): EventIssueRow[] {
  const q = (f.q ?? '').trim().toLocaleLowerCase();
  return data.issues.filter((row) => {
    if (f.eventId && row.event_id !== f.eventId) return false;
    if (f.hideInternal && row.internal) return false;
    if (f.hideWithdrawn && row.withdrawn) return false;
    if (f.status && f.status !== 'all' && issueStatus(row) !== f.status) return false;
    const p = parseEventIssue(row.text);
    if (f.kind && f.kind !== 'all' && p.kind !== f.kind) return false;
    if (f.field && f.field !== 'all' && p.field !== f.field) return false;
    if (q) {
      const ev = data.events[row.event_id];
      const hay = [p.summary, row.text, ev?.title, row.who, row.username,
        ev?.short_id != null ? `#${ev.short_id}` : null, ev?.source, ev?.city]
        .filter(Boolean).join(' ').toLocaleLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

/** رخدادها به ترتیبِ «بیشترین پرچمِ باز»، بعد تازه‌ترین پرچم. */
export interface EventIssueGroup {
  eventId: string;
  rows: EventIssueRow[];
  open: number;
  latest: string;
}

export function groupByEvent(rows: EventIssueRow[]): EventIssueGroup[] {
  const map = new Map<string, EventIssueGroup>();
  for (const row of rows) {
    const g = map.get(row.event_id) ?? { eventId: row.event_id, rows: [], open: 0, latest: row.at };
    g.rows.push(row);
    if (issueStatus(row) === 'open' && isActionable(row)) g.open += 1;
    if (row.at > g.latest) g.latest = row.at;
    map.set(row.event_id, g);
  }
  return [...map.values()].sort((a, b) => b.open - a.open || b.latest.localeCompare(a.latest));
}

/* ─────────────────────────── ۴ ــ آمار ─────────────────────────── */

export interface StatusCounts { open: number; accepted: number; rejected: number; total: number }

const emptyCounts = (): StatusCounts => ({ open: 0, accepted: 0, rejected: 0, total: 0 });

export interface CountRow extends StatusCounts {
  key: string;
  label: string;
  /** سهمِ تاییدشده از تصمیم‌گرفته‌ها؛ `null` وقتی هنوز تصمیمی نیست. */
  acceptRate: number | null;
}

export interface DayRow extends StatusCounts { day: string }

export interface IssueStats {
  totals: StatusCounts & {
    fixed: number;
    withdrawn: number;
    events: number;
    reporters: number;
    internal: number;
  };
  acceptRate: number | null;
  /** ساعت از ثبتِ پرچم تا اولین تصمیم. */
  decideHours: { median: number | null; p90: number | null; n: number };
  /** کهنه‌ترین پرچمِ بازِ زنده، به روز. */
  oldestOpenDays: number | null;
  byKind: CountRow[];
  byField: CountRow[];
  /** فقط تگ‌ها: کدام تگ بیشتر از همه «غلط» یا «جاافتاده» گفته شده. */
  byTag: CountRow[];
  bySource: CountRow[];
  byCity: CountRow[];
  byDay: DayRow[];
  topEvents: (CountRow & { eventId: string })[];
  topReporters: CountRow[];
  /** ستون‌هایی که از راهِ این سربرگ اصلاح شدند. */
  fixedFields: { key: string; n: number }[];
}

const DAY = 24 * 60 * 60 * 1000;

const quantile = (sorted: number[], q: number): number | null => {
  if (!sorted.length) return null;
  const at = (sorted.length - 1) * q;
  const lo = Math.floor(at);
  const hi = Math.ceil(at);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (at - lo);
};

const rate = (c: StatusCounts): number | null =>
  c.accepted + c.rejected > 0 ? c.accepted / (c.accepted + c.rejected) : null;

function bump(map: Map<string, StatusCounts & { label: string }>, key: string, label: string, st: IssueStatus) {
  const c = map.get(key) ?? { ...emptyCounts(), label };
  c[st] += 1;
  c.total += 1;
  map.set(key, c);
}

const toRows = (map: Map<string, StatusCounts & { label: string }>, limit?: number): CountRow[] =>
  [...map.entries()]
    .map(([key, c]) => ({ key, ...c, acceptRate: rate(c) }))
    .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label))
    .slice(0, limit ?? Infinity);

/** `YYYY-MM-DD` به وقتِ UTC — همان کلیدی که محورِ نمودار می‌سازد. */
const dayKey = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/**
 * همه‌ی عددهای بخشِ آمار، روی سطرهایی که **همین حالا** فیلتر شده‌اند — پس
 * «فقط تگ‌ها» یا «بی حساب‌های تستی» آمار را هم با خودش عوض می‌کند.
 *
 * `days` طولِ نمودارِ روزانه است؛ روزِ بی‌پرچم هم ستونِ صفر می‌گیرد تا جای
 * خالی از «نبود» جدا باشد.
 */
export function buildIssueStats(
  data: EventIssuesData, rows: EventIssueRow[], opts: { now?: number; days?: number } = {},
): IssueStats {
  const now = opts.now ?? Date.now();
  const days = Math.max(1, opts.days ?? 30);
  const totals = { ...emptyCounts(), fixed: 0, withdrawn: 0, events: 0, reporters: 0, internal: 0 };
  const byKind = new Map<string, StatusCounts & { label: string }>();
  const byField = new Map<string, StatusCounts & { label: string }>();
  const byTag = new Map<string, StatusCounts & { label: string }>();
  const bySource = new Map<string, StatusCounts & { label: string }>();
  const byCity = new Map<string, StatusCounts & { label: string }>();
  const byEvent = new Map<string, StatusCounts & { label: string }>();
  const byReporter = new Map<string, StatusCounts & { label: string }>();
  const fixed = new Map<string, number>();
  const decide: number[] = [];
  let oldestOpen: number | null = null;

  const start = Date.parse(`${dayKey(now - (days - 1) * DAY)}T00:00:00Z`);
  const byDay = new Map<string, DayRow>();
  for (let i = 0; i < days; i += 1) {
    const day = dayKey(start + i * DAY);
    byDay.set(day, { day, ...emptyCounts() });
  }

  for (const row of rows) {
    const st = issueStatus(row);
    const p = parseEventIssue(row.text);
    const ev = data.events[row.event_id];
    totals[st] += 1;
    totals.total += 1;
    if (row.fixed_at) totals.fixed += 1;
    if (row.withdrawn) totals.withdrawn += 1;
    if (row.internal) totals.internal += 1;

    bump(byKind, p.kind, KIND_LABEL[p.kind], st);
    if (p.field) bump(byField, p.field, FIELD_LABEL[p.field] ?? p.field, st);
    if (p.field === 'tag' && p.value) {
      bump(byTag, `${p.kind}:${p.value}`, `${p.kind === 'wrong' ? '✏️' : '➕'} ${p.value}`, st);
    }
    bump(bySource, ev?.source ?? '—', ev?.source ?? 'نامعلوم', st);
    bump(byCity, ev?.city ?? '—', ev?.city ?? 'نامعلوم', st);
    bump(byEvent, row.event_id, ev?.title ?? row.event_id, st);
    const reporter = row.user_id ?? '—';
    bump(byReporter, reporter,
      [row.who, row.username ? `@${row.username}` : null].filter(Boolean).join(' ') || 'بی‌نام', st);

    for (const f of row.fixed_fields ?? []) fixed.set(f, (fixed.get(f) ?? 0) + 1);

    const at = Date.parse(row.at);
    if (row.decided_at) decide.push((Date.parse(row.decided_at) - at) / 3_600_000);
    if (st === 'open' && isActionable(row)) {
      const age = (now - at) / DAY;
      oldestOpen = oldestOpen == null ? age : Math.max(oldestOpen, age);
    }
    const d = byDay.get(dayKey(at));
    if (d) { d[st] += 1; d.total += 1; }
  }

  totals.events = byEvent.size;
  totals.reporters = byReporter.size;
  decide.sort((a, b) => a - b);

  return {
    totals,
    acceptRate: rate(totals),
    decideHours: { median: quantile(decide, 0.5), p90: quantile(decide, 0.9), n: decide.length },
    oldestOpenDays: oldestOpen,
    byKind: toRows(byKind),
    byField: toRows(byField),
    byTag: toRows(byTag, 15),
    bySource: toRows(bySource, 15),
    byCity: toRows(byCity, 15),
    byDay: [...byDay.values()],
    topEvents: toRows(byEvent, 10).map((r) => ({ ...r, eventId: r.key })),
    topReporters: toRows(byReporter, 10),
    fixedFields: [...fixed.entries()].map(([key, n]) => ({ key, n })).sort((a, b) => b.n - a.n),
  };
}

/* ─────────────────────────── ۵ ــ اصلاح ─────────────────────────── */

/** ستون‌هایی که `admin_fix_event` در `p_patch` می‌پذیرد. */
export type FixColumn =
  | 'title' | 'description' | 'starts_at' | 'ends_at' | 'is_free' | 'price_note'
  | 'url' | 'address' | 'image_url' | 'ticket_url';

/** فیلدِ پرچم ← ستون‌هایی که جوابش‌اند. `tags` یعنی `p_tag_ids`. */
export const FIELD_COLUMNS: Record<string, (FixColumn | 'tags')[]> = {
  title: ['title'],
  image: ['image_url'],
  description: ['description'],
  description_format: ['description'],
  when: ['starts_at', 'ends_at'],
  where: ['address'],
  price: ['is_free', 'price_note'],
  ticket: ['ticket_url'],
  link: ['url'],
  tag: ['tags'],
};

/** فرمِ اصلاح، همه رشته — همان چیزی که کادرها نگه می‌دارند. */
export interface FixDraft {
  title: string;
  description: string;
  /** `YYYY-MM-DDTHH:mm` به وقتِ مرورگر — مقدارِ `<input type="datetime-local">`. */
  starts_at: string;
  ends_at: string;
  is_free: boolean;
  price_note: string;
  url: string;
  address: string;
  image_url: string;
  ticket_url: string;
  tag_ids: number[];
}

const pad = (n: number) => String(n).padStart(2, '0');

/** ISO → مقدارِ `datetime-local` به وقتِ همین مرورگر. */
export function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** وارونِ `toLocalInput`؛ رشته‌ی خالی یعنی `null`. */
export function fromLocalInput(v: string): string | null {
  if (!v.trim()) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function draftOf(ev: EventIssueEvent): FixDraft {
  return {
    title: ev.title ?? '',
    description: ev.description ?? '',
    starts_at: toLocalInput(ev.starts_at),
    ends_at: toLocalInput(ev.ends_at),
    is_free: !!ev.is_free,
    price_note: ev.price_note ?? '',
    url: ev.url ?? '',
    address: ev.address ?? '',
    image_url: ev.image_url ?? '',
    ticket_url: ev.ticket_url ?? '',
    tag_ids: [...(ev.tag_ids ?? [])],
  };
}

/** متنِ خالی = `null`، همان کاری که تابعِ SQL هم می‌کند. */
const textOrNull = (v: string): string | null => (v.trim() ? v.trim() : null);

/** دو لحظه‌ی یکسان با دو نوشتنِ متفاوت («…Z» و «…+00:00») عوض‌شده شمرده نشوند. */
const sameInstant = (a: string | null, b: string | null): boolean =>
  a === b || (!!a && !!b && Date.parse(a) === Date.parse(b));

/**
 * فقط آنچه واقعاً عوض شد. کلیدی که نیست یعنی «دست نزن» — پس دو ادمین که
 * هم‌زمان دو فیلدِ مختلف را اصلاح کنند، کارِ هم را پاک نمی‌کنند.
 *
 * توضیحات عمداً `trim` نمی‌شود: «قالبِ توضیحات» (خطِ خالی، پاراگراف) همان
 * چیزی است که ممکن است اصلاح شود.
 */
export function buildFixPatch(ev: EventIssueEvent, d: FixDraft): {
  patch: Partial<Record<FixColumn, string | boolean | null>>;
  tagIds: number[] | null;
  changed: (FixColumn | 'tags')[];
} {
  const patch: Partial<Record<FixColumn, string | boolean | null>> = {};
  const setText = (col: 'title' | 'price_note' | 'url' | 'address' | 'image_url' | 'ticket_url') => {
    const next = textOrNull(d[col]);
    if (next !== (ev[col] ?? null)) patch[col] = next;
  };
  setText('title');
  const desc = d.description.trim() ? d.description : null;
  if (desc !== (ev.description ?? null)) patch.description = desc;
  const starts = fromLocalInput(d.starts_at);
  if (!sameInstant(starts, ev.starts_at) && toLocalInput(starts) !== toLocalInput(ev.starts_at)) patch.starts_at = starts;
  const ends = fromLocalInput(d.ends_at);
  if (!sameInstant(ends, ev.ends_at) && toLocalInput(ends) !== toLocalInput(ev.ends_at)) patch.ends_at = ends;
  if (d.is_free !== !!ev.is_free) patch.is_free = d.is_free;
  setText('price_note');
  setText('url');
  setText('address');
  setText('image_url');
  setText('ticket_url');

  const a = [...new Set(d.tag_ids)];
  const b = [...(ev.tag_ids ?? [])];
  // مجموعه مقایسه می‌شود، نه ترتیب: جابه‌جا کردنِ دو تگ اصلاحی نیست.
  const sameTags = a.length === b.length
    && [...a].sort((x, y) => x - y).join() === [...b].sort((x, y) => x - y).join();
  const tagIds = sameTags ? null : a;
  const changed = [...Object.keys(patch) as FixColumn[], ...(tagIds ? ['tags' as const] : [])];
  return { patch, tagIds, changed };
}

/** چیزهایی که جلوی ذخیره را می‌گیرند — همان قیدهای دیتابیس، پیش از رفتن. */
export function fixProblems(d: FixDraft): string[] {
  const out: string[] = [];
  if (!d.title.trim()) out.push('عنوان نمی‌تواند خالی باشد.');
  for (const [col, label] of [['url', 'لینکِ منبع'], ['ticket_url', 'لینکِ بلیط'], ['image_url', 'عکس']] as const) {
    const v = d[col].trim();
    if (v && !/^https?:\/\/\S+$/i.test(v)) out.push(`${label} باید با http:// یا https:// شروع شود.`);
  }
  const s = fromLocalInput(d.starts_at);
  const e = fromLocalInput(d.ends_at);
  if (s && e && Date.parse(e) < Date.parse(s)) out.push('پایان پیش از شروع است.');
  return out;
}

/* ─────────────────────────── تگ‌ها ─────────────────────────── */

const norm = (s: string) => s.replace(/\s+/g, ' ').trim().toLocaleLowerCase();

/**
 * تگی که کاربر با نامش پرچم زده.
 *
 * پرچم برچسبِ **نمایشیِ** تگ را در زبانِ همان کاربر ذخیره می‌کند («🎷 جاز»
 * یا «🎷 Jazz»)، نه شناسه‌اش. پس با هر برچسبِ هر زبان، با و بی ایموجی،
 * مقایسه می‌شود. پیدا نشدن (تگی که کاربر خودش ساخته، یا برچسبی که بعداً
 * عوض شده) `null` است و رابط همان را می‌گوید.
 */
export function tagForLabel(tags: Tag[], label: string | null): Tag | null {
  if (!label) return null;
  const want = norm(label);
  for (const tag of tags) {
    const names = [tag.labelFa, tag.labelEn, tag.slug, ...Object.values(tag.labels ?? {})].filter(Boolean);
    for (const n of names) {
      if (norm(n) === want || norm(`${tag.emoji} ${n}`) === want) return tag;
    }
  }
  return null;
}

/**
 * پیشنهادِ تگ‌ها از روی پرچم‌های تگ: «اشتباه» برداشته، «جاافتاده» اضافه.
 * فقط پیشنهاد است — رابط نشانش می‌دهد و ادمین تصمیم می‌گیرد.
 */
export function suggestTagIds(current: number[], rows: EventIssueRow[], tags: Tag[]): number[] {
  const out = [...current];
  for (const row of rows) {
    const p = parseEventIssue(row.text);
    if (p.field !== 'tag') continue;
    const tag = tagForLabel(tags, p.value);
    if (!tag) continue;
    if (p.kind === 'wrong') {
      const i = out.indexOf(tag.id);
      if (i >= 0) out.splice(i, 1);
    } else if (p.kind === 'missed' && !out.includes(tag.id)) {
      out.push(tag.id);
    }
  }
  return out;
}
